import React, { useCallback, useMemo, useState, FocusEvent, useRef } from 'react';
import { useFilter } from 'react-aria';
import { useMenuTriggerState } from 'react-stately';

import { TagsWrapper } from '../TagsWrapper/TagsWrapper';
import { List, ListItem, ListStubItem, ListTreeItem } from '../../../List';
import { Drawer, DrawerHeader } from '../../../Drawer';
import { TreeNode, traverseTreeChildren, useCheckboxTreeState } from '../../../../hooks/useTreeState';
import { cn } from '../../../../lib/css/className/className';
import { getSizeSkin } from '../../../../lib/skin/getSizeSkin/getSizeSkin';
import { TagTextInput } from '../TagTextInput/TagTextInput';
import { useVStack } from '../../../../hooks/useStack';
import { ColorlessIconProps, IconAlert, IconArrowLeft, IconArrowRight, IconRefresh } from '../../../../icons';
import { ClickableIcon } from '../../../ClickableIcon';
import { Spinner } from '../../../Spinner';
import { HStack } from '../../../Stack';

import { TagTreeEditorProps } from './types';
import './TagTreeEditor@mobile.css';

const emptyFilterFn = () => true;
const cls = cn('TagTreeEditor');

const TreeItem = function TreeItem({
    item,
    isDisabled,
    isChecked,
    isIndeterminate,
    isLoading,
    hasLoadError,
    onRetryLoadChildren,
    setChecked,
    setOpened,
    inputValue,
    'data-testid': dataTestId,
}: {
    item: TreeNode;
    isDisabled?: boolean;
    isChecked: boolean;
    isIndeterminate?: boolean;
    isLoading?: boolean;
    hasLoadError?: boolean;
    onRetryLoadChildren?: (nodeId: string) => void;
    setChecked: (checked: boolean) => void;
    setOpened: (itemId: string) => void;
    inputValue?: string;
    'data-testid': string;
}) {
    const canExpand = item.isExpandable !== false && (Boolean(item.children?.length) || item.isExpandable === true);

    const onItemClick = useCallback(() => {
        if (!inputValue && canExpand) {
            setOpened(item.id);
        }
    }, [inputValue, canExpand, item.id, setOpened]);

    const onRetryLoad = useCallback(() => {
        onRetryLoadChildren?.(item.id);
    }, [item.id, onRetryLoadChildren]);

    const renderIconRight = useCallback(
        (iconProps: ColorlessIconProps) => {
            return (
                <ClickableIcon
                    className={cls('iconRight', [iconProps.className])}
                    color={iconProps.color}
                    onClick={onItemClick}
                    data-testid={`${dataTestId}.${item.id}.ExpandIcon`}
                >
                    <IconArrowRight {...iconProps} />
                </ClickableIcon>
            );
        },
        [dataTestId, item.id, onItemClick],
    );

    const stateAddonRight = useMemo(() => {
        if (isLoading) {
            return (
                <div className={cls('iconRight')}>
                    <Spinner size="24" color="gray" data-testid={`${dataTestId}.${item.id}.Spinner`} />
                </div>
            );
        }

        if (hasLoadError) {
            return (
                <HStack gap="4" alignItems="center" className={cls('iconRight')}>
                    <IconAlert
                        size="24"
                        color="current"
                        className={cls('errorIcon')}
                        data-testid={`${dataTestId}.${item.id}.ErrorIcon`}
                    />
                    <ClickableIcon
                        color="current"
                        onClick={onRetryLoad}
                        data-testid={`${dataTestId}.${item.id}.RetryLoad`}
                    >
                        <IconRefresh size="24" />
                    </ClickableIcon>
                </HStack>
            );
        }

        return null;
    }, [dataTestId, hasLoadError, isLoading, item.id, onRetryLoad]);

    const hasIconRight = !inputValue && canExpand;
    const hasStateAddon = !inputValue && (isLoading || hasLoadError);
    // Пока активен индикатор статуса (loading/error) — стрелку справа не показываем,
    // её место занимает stateAddonRight, чтобы не было визуальной каши из двух иконок.
    const iconRightProp = !hasStateAddon && hasIconRight ? renderIconRight : undefined;
    const addonRightProp = hasStateAddon ? stateAddonRight : undefined;

    if (item.isSelectable === false) {
        return (
            <ListItem
                key={item.id}
                isDisabled={isDisabled}
                onClick={inputValue || isLoading || hasLoadError ? undefined : onItemClick}
                caption={inputValue ? item.parent?.label : undefined}
                iconRight={iconRightProp}
                addonRight={addonRightProp}
                data-testid={`${dataTestId}.${item.id}`}
            >
                {item.label}
            </ListItem>
        );
    }

    return (
        <ListTreeItem
            key={item.id}
            level={0}
            hasExpandIcon={false}
            isDisabled={isDisabled}
            isSelected={isChecked}
            isIndeterminate={isIndeterminate}
            hasCheckbox={item.isSelectable}
            onChange={setChecked}
            caption={inputValue ? item.parent?.label : undefined}
            iconRight={iconRightProp}
            addonRight={addonRightProp}
            data-testid={`${dataTestId}.${item.id}`}
        >
            {item.label}
        </ListTreeItem>
    );
};

export const TagTreeEditor = function TagTreeEditor(props: TagTreeEditorProps) {
    const {
        nodes,
        initialCheckboxValue,
        changedNodes,
        setChangedNodes,
        emptySection,
        filter = 'contains',
        label,
        tags,
        onRemove,
        inputValue,
        onChange,
        onBlur,
        maxInputHeight,
        childrenLoadingMap,
        childrenErrorMap,
        indeterminateMap,
        onExpandNode,
        onRetryLoadChildren,
        onSearch,
        isSearching,
        searchMatchedIds,
        'data-testid': dataTestId = 'TagTreeEditor',
    } = props;

    const trimmedInputValue = inputValue?.trim() ?? '';
    // Внешний поиск считаем активным, когда консьюмер вернул searchMatchedIds — пока undefined
    const isExternalSearchResultsReady =
        Boolean(onSearch) && trimmedInputValue.length > 0 && searchMatchedIds !== undefined;
    const isLocalSearchActive = !onSearch && Boolean(inputValue);
    const isInSearchMode = isExternalSearchResultsReady || isLocalSearchActive;

    const initialChangedNodes = useRef(changedNodes);
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const stackClass = useVStack({ gap: '16' });
    const sizeSkinClass = getSizeSkin('m');
    const isDrawerAllowed = nodes.length > 0 || Boolean(props.emptySection);
    const { close, isOpen } = useMenuTriggerState({
        defaultOpen: isDrawerAllowed,
        onOpenChange: isOpen => {
            if (!isOpen) {
                setTimeout(() => {
                    onBlur?.();
                });
            }
        },
    });
    const [openedNodesStack, setOpenedNodesStack] = useState<string[]>([]);

    const nodesMap = useMemo(() => {
        const result = new Map<string, TreeNode>();
        traverseTreeChildren(nodes, item => result.set(item.id, item));

        return result;
    }, [nodes]);

    const { renderTree } = useCheckboxTreeState({
        nodes: nodes,
        initialCheckboxValue,
        changedNodes,
        setChangedNodes,
        renderCollapsed: true,
    });

    const onInputChange = useCallback(
        (value: string) => {
            onChange(value);
            onSearch?.(value);
        },
        [onChange, onSearch],
    );

    const onInputBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
        if (event.relatedTarget instanceof HTMLElement && popupRef.current?.contains(event.relatedTarget)) {
            inputRef.current?.focus();
            return;
        }
    }, []);

    const handleCancel = useCallback(() => {
        close();
        setChangedNodes(() => [...initialChangedNodes.current]);
    }, [close, setChangedNodes, initialChangedNodes]);

    const defaultFilter = useFilter({ sensitivity: 'base' });
    const appliedFilter = (() => {
        if (filter === 'none') {
            return emptyFilterFn;
        }
        if (typeof filter === 'string') {
            return defaultFilter[filter];
        }
        return filter;
    })();

    const onBackClick = useCallback(() => {
        const backNodeId = openedNodesStack.at(-1);
        setOpenedNodesStack(openedNodesStack.slice(0, -1));
        if (backNodeId !== undefined) {
            onExpandNode?.(backNodeId, false);
        }
    }, [onExpandNode, openedNodesStack]);

    const lastNodeId = openedNodesStack.at(-1);
    const currentNode = lastNodeId ? nodesMap.get(lastNodeId) : null;

    const setOpened = useCallback(
        (itemId: string) => {
            setOpenedNodesStack(prevValue => prevValue.concat(itemId));
            onExpandNode?.(itemId, true);
        },
        [onExpandNode],
    );

    const onParentRetryClick = useCallback(() => {
        if (!currentNode?.id) return;
        onRetryLoadChildren?.(currentNode?.id);
    }, [currentNode?.id, onRetryLoadChildren]);

    const drawerContent = useMemo(() => {
        const isParentHasError = currentNode ? Boolean(childrenErrorMap?.[currentNode.id]) : false;

        const isCurrentChildrenLoading =
            !isInSearchMode && lastNodeId !== undefined && Boolean(childrenLoadingMap?.[lastNodeId]);
        const childrenLoader = isCurrentChildrenLoading ? <Spinner size="24" color="gray" /> : null;

        let parentAddonRight: React.ReactNode = null;
        if (isCurrentChildrenLoading) {
            parentAddonRight = <Spinner size="24" color="gray" data-testid={`${dataTestId}.Parent.Spinner`} />;
        } else if (isParentHasError) {
            parentAddonRight = (
                <HStack gap="4" alignItems="center">
                    <IconAlert
                        size="24"
                        color="current"
                        className={cls('errorIcon')}
                        data-testid={`${dataTestId}.Parent.ErrorIcon`}
                    />
                    <ClickableIcon
                        color="current"
                        logNode="RetryLoadChildren"
                        data-testid={`${dataTestId}.Parent.RetryLoad`}
                        clickableAreaSize="24"
                        onClick={onParentRetryClick}
                    >
                        <IconRefresh size="24" />
                    </ClickableIcon>
                </HStack>
            );
        }

        const parentItem =
            !isInSearchMode && currentNode ? (
                <ListItem
                    key={currentNode.id}
                    onClick={onBackClick}
                    iconLeft={IconArrowLeft}
                    data-testid={`${dataTestId}.BackButton`}
                    addonRight={parentAddonRight}
                >
                    {currentNode.label}
                </ListItem>
            ) : null;

        const shouldShowItem = (item: TreeNode, level: number): boolean => {
            if (item.parent && childrenLoadingMap?.[item.parent.id]) return false;

            // 1) Поиск отдал результат — плоский список совпавших узлов.
            //    Узлы-предки, пришедшие в nodes ради иерархии, отсекаем по searchMatchedIds.
            if (isExternalSearchResultsReady) {
                return Boolean(searchMatchedIds && searchMatchedIds[item.id]);
            }

            // 2) Локальный фильтр (onSearch не задан) — рендерим всё, что прошло по label
            if (isLocalSearchActive) {
                return appliedFilter(item.label, inputValue ?? '');
            }

            // 3) Режим навигации — корневой уровень или дети текущего раскрытого узла
            return (level === 1 && !lastNodeId) || (Boolean(lastNodeId) && lastNodeId === item.parent?.id);
        };

        const renderedTree = renderTree(({ level, item, isChecked, isDisabled, setChecked }) => {
            if (!shouldShowItem(item, level)) return null;
            return (
                <TreeItem
                    item={item}
                    isDisabled={isDisabled}
                    isChecked={isChecked}
                    isIndeterminate={Boolean(indeterminateMap?.[item.id])}
                    isLoading={Boolean(childrenLoadingMap?.[item.id])}
                    hasLoadError={Boolean(childrenErrorMap?.[item.id])}
                    onRetryLoadChildren={onRetryLoadChildren}
                    setChecked={setChecked}
                    setOpened={setOpened}
                    inputValue={isInSearchMode ? inputValue : ''}
                    data-testid={dataTestId}
                />
            );
        });

        return (
            nodes.length !== 0 &&
            (parentItem || childrenLoader || renderedTree) && (
                <>
                    {parentItem}
                    {!isParentHasError && renderedTree}
                </>
            )
        );
    }, [
        appliedFilter,
        childrenErrorMap,
        childrenLoadingMap,
        currentNode,
        dataTestId,
        indeterminateMap,
        inputValue,
        isInSearchMode,
        isExternalSearchResultsReady,
        isLocalSearchActive,
        lastNodeId,
        nodes.length,
        onBackClick,
        onParentRetryClick,
        onRetryLoadChildren,
        renderTree,
        searchMatchedIds,
        setOpened,
    ]);

    return (
        <>
            <div className={cls('pseudoInput', [props.className, sizeSkinClass])} />
            <Drawer
                ref={popupRef}
                className={stackClass}
                isOpen={isOpen}
                onClose={close}
                header={
                    <DrawerHeader
                        title={label}
                        onCancelClick={handleCancel}
                        onSaveClick={close}
                        data-testid={`${dataTestId}.Header`}
                    />
                }
                data-testid={`${dataTestId}.Popup`}
                isFullscreen
            >
                <TagsWrapper
                    tags={tags ?? []}
                    onRemove={onRemove}
                    data-testid={`${dataTestId}.Drawer.Field`}
                    maxInputHeight={maxInputHeight}
                >
                    <TagTextInput
                        {...props}
                        tagGroupSize={'m'}
                        ref={inputRef}
                        onFocus={undefined}
                        onBlur={onInputBlur}
                        value={inputValue}
                        onChange={onInputChange}
                        autoFocus
                        data-testid={`${dataTestId}.TextInput`}
                    />
                </TagsWrapper>
                <List
                    size="m"
                    width="max"
                    data-testid={`${dataTestId}.List`}
                    // нужно для сброса скролла
                    key={currentNode?.id}
                >
                    {isSearching ? (
                        <ListStubItem width="100%" data-testid={`${dataTestId}.SearchingStub`} />
                    ) : (
                        drawerContent || emptySection
                    )}
                </List>
            </Drawer>
        </>
    );
};

export default TagTreeEditor;
